import { useQuery } from '@tanstack/react-query';
import { ActivityLogs, type ActivityLogEntry } from './api';
import { useBackendOnline } from '@/store/connection';

/**
 * Düzleştirilmiş ve UI-ready aktivite kaydı. ActivityLogEntry ham backend
 * formatından farklı — burada call event'leri tek satıra collapsed,
 * mute/hold/dtmf gibi gürültüler özet meta olarak iliştirilmiş.
 */
export type ActivityKind =
  | 'call'         // inbound/outbound/missed birleşik
  | 'queue'        // queue_login / queue_logout
  | 'status'       // janus_registered, agent state changes
  | 'transfer';    // call_transferred (call_ended ile birleşmediği için ayrı)

export interface ActivityItem {
  /** Stable key for React lists. Genellikle ilk event'in id'si. */
  key: string;
  kind: ActivityKind;
  /** Call kaydı için 'inbound'/'outbound'/'missed', diğer kayıtlar için undefined */
  direction?: 'inbound' | 'outbound' | 'missed';
  /** UI'da gösterilecek pill etiketi (Gelen / Giden / Kuyruğa girdi / vb.) */
  label: string;
  /** Ana açıklama (telefon numarası, durum metni). */
  primary: string;
  /** İkinci satır metni (cevaplandı / cevapsız / *61 ile, vb.) */
  secondary?: string;
  /** Saniye cinsinden süre (call_ended duration_ms'den). */
  durationSec?: number;
  /** Çağrı içindeki mute/hold süresi özeti (sadece call kayıtlarında). */
  callSummary?: { muteSec?: number; holdSec?: number };
  /** "14:32" formatında lokal saat. */
  startedAt: number;
}

/** Aynı callId etrafındaki tüm event'leri tek bir ActivityItem'a indirger. */
function collapseCallEvents(logs: ActivityLogEntry[]): Map<string, ActivityItem> {
  const calls = new Map<string, ActivityItem>();
  for (const l of logs) {
    const meta = (l.meta ?? {}) as Record<string, any>;
    const key = l.refId ?? `synth-${l.id}`;

    if (l.type === 'incoming_call') {
      calls.set(key, {
        key: String(l.id),
        kind: 'call',
        direction: 'inbound',
        label: 'Gelen',
        primary: String(meta.peer ?? meta.display ?? ''),
        secondary: 'cevap bekleniyor',
        startedAt: new Date(l.createdAt).getTime(),
      });
    } else if (l.type === 'call_started') {
      calls.set(key, {
        key: String(l.id),
        kind: 'call',
        direction: 'outbound',
        label: 'Giden',
        primary: String(meta.target ?? ''),
        secondary: 'aranıyor',
        startedAt: new Date(l.createdAt).getTime(),
      });
    } else if (l.type === 'call_answered' && calls.has(key)) {
      const c = calls.get(key)!;
      c.secondary = 'cevaplandı';
    } else if (l.type === 'call_rejected' && calls.has(key)) {
      const c = calls.get(key)!;
      c.direction = 'missed';
      c.label = 'Reddedildi';
      c.secondary = 'reddedildi';
    } else if (l.type === 'call_ended' && calls.has(key)) {
      const c = calls.get(key)!;
      if (typeof meta.duration_ms === 'number') {
        c.durationSec = Math.round(meta.duration_ms / 1000);
      }
      if (meta.direction === 'inbound' || meta.direction === 'outbound') {
        c.direction = meta.direction;
        c.label = meta.direction === 'inbound' ? 'Gelen' : 'Giden';
      }
      if (!c.primary && meta.peer) c.primary = String(meta.peer);
      // Cevapsız mı tespiti: duration 0 veya çok kısa
      const dur = c.durationSec ?? 0;
      if (c.direction === 'inbound' && dur < 1) {
        c.direction = 'missed';
        c.label = 'Kaçan';
        c.secondary = 'cevapsız';
      } else {
        c.secondary = `${formatDur(dur)} görüşme`;
      }
      // mute/hold özet
      const muteSec = typeof meta.mute_seconds === 'number' ? meta.mute_seconds : undefined;
      const holdSec = typeof meta.hold_seconds === 'number' ? meta.hold_seconds : undefined;
      if (muteSec || holdSec) c.callSummary = { muteSec, holdSec };
    } else if (l.type === 'call_transferred' && calls.has(key)) {
      const c = calls.get(key)!;
      c.secondary = `→ ${meta.to ?? '?'} aktarıldı`;
    }
  }
  return calls;
}

/** queue_paused event meta.reason → display label. Bu map BREAK_REASONS
 *  (BreakReasonPopover.tsx) ile sync olmalı — burada duplicate aşağıdaki
 *  yorum'da neden duplicate dedik:
 *
 *  BreakReasonPopover'dan reasonLabel() import etmek React component
 *  import'u getirir useActivityFeed (saf hook) içine — circular bağımlılık
 *  oluşmaz ama temiz değil. Birkaç string için ayrı tutmak makul.
 */
const REASON_LABELS: Record<string, string> = {
  'mola':          'Kısa Mola',
  'yemek':         'Yemek',
  'dis-arama':     'Dış Arama',
  'egitim':        'Eğitim',
  'toplanti':      'Toplantı',
  'sinav':         'Sınav',
  'soru':          'Soru Molası',
  'geri-bildirim': 'Geri Bildirim',
};

/** Aktivite log'larından non-call event'leri çıkar (queue, status). */
function extractNonCallEvents(logs: ActivityLogEntry[]): ActivityItem[] {
  // Çift-satır önleme: agent bir queue aksiyonu yapınca (Molaya girdi/Kuyruğa
  // girdi…) backend state-poller ~10s içinde aynı geçişi 3CX status değişimi
  // olarak da loglar (pbx_state_change) → feed'de "Molaya girdi · Yemek" + "Yemekte"
  // gibi ÇİFT satır çıkıyordu. Queue aksiyonunu (zengin, sebepli) tut, onu
  // yankılayan pbx_state_change'i ele. Dış kaynaklı (3CX web client / lider)
  // status değişimleri queue aksiyonu olmadığı için KALIR.
  const DEDUP_MS = 15_000;
  const queueActionTs: number[] = [];
  for (const l of logs) {
    if (l.type === 'queue_login' || l.type === 'queue_logout'
      || l.type === 'queue_paused' || l.type === 'queue_resumed') {
      queueActionTs.push(new Date(l.createdAt).getTime());
    }
  }
  const echoesQueueAction = (ts: number) =>
    queueActionTs.some((q) => ts >= q && ts - q <= DEDUP_MS);

  const items: ActivityItem[] = [];
  for (const l of logs) {
    const meta = (l.meta ?? {}) as Record<string, any>;
    const startedAt = new Date(l.createdAt).getTime();
    if (l.type === 'queue_login') {
      items.push({
        key: String(l.id), kind: 'queue', label: 'Kuyruğa girdi',
        primary: 'Çağrı almaya hazır', secondary: meta.code ? `${meta.code} ile` : undefined,
        startedAt,
      });
    } else if (l.type === 'queue_logout') {
      items.push({
        key: String(l.id), kind: 'queue', label: 'Kuyruktan çıktı',
        primary: 'Çağrı almıyor', secondary: meta.code ? `${meta.code} ile` : undefined,
        startedAt,
      });
    } else if (l.type === 'queue_paused') {
      // Sebep meta.reason'da — BREAK_REASONS map'inden label çevir
      const reasonKey = typeof meta.reason === 'string' ? meta.reason : null;
      const reasonLabel = reasonKey ? REASON_LABELS[reasonKey] ?? reasonKey : 'Kısa mola';
      items.push({
        key: String(l.id), kind: 'queue', label: 'Molaya girdi',
        primary: reasonLabel, secondary: meta.code ? `${meta.code} ile` : undefined,
        startedAt,
      });
    } else if (l.type === 'queue_resumed') {
      items.push({
        key: String(l.id), kind: 'queue', label: 'Moladan döndü',
        primary: 'Çağrı almaya hazır', secondary: meta.code ? `${meta.code} ile` : undefined,
        startedAt,
      });
    } else if (l.type === 'janus_registered') {
      // Net kelime yeterli — "SIP register başarılı" teknik detayı kaldırıldı.
      items.push({
        key: String(l.id), kind: 'status', label: 'Bağlandı',
        primary: '', startedAt,
      });
    } else if (l.type === 'pbx_state_change') {
      // Bir queue aksiyonunun yankısıysa ATLA (çift satır olmasın). Dış kaynaklı
      // status değişimleri (yakınında queue aksiyonu yok) gösterilmeye devam eder.
      if (echoesQueueAction(startedAt)) continue;
      // 3CX-side durum değişimi. Sadece net durum kelimesi (Hazır/Çevrimdışı/
      // Molada…); "3CX durum" + "güncellendi" gürültüsü yok. Özel durumda
      // (Eğitim/Toplantı…) alt-mesaj primary olarak gösterilir.
      const statusKey = typeof meta.status === 'string' ? meta.status : null;
      const label = statusKey ? (PBX_STATUS_LABELS[statusKey] ?? statusKey) : 'Durum değişti';
      const msg = typeof meta.profileMessage === 'string' && meta.profileMessage ? meta.profileMessage : '';
      items.push({
        key: String(l.id), kind: 'status', label,
        primary: msg,
        startedAt,
      });
    }
  }
  return items;
}

/** pbx_state_change meta.status (AgentLiveStatus) → görünen etiket. */
const PBX_STATUS_LABELS: Record<string, string> = {
  'hazir':       'Hazır',
  'mola':        'Molada',
  'yemek':       'Yemekte',
  'dis-arama':   'Dış Arama',
  'ozel':        'Özel durum',
  'kuyruk-disi': 'Kuyruk dışı',
  'cevrimdisi':  'Çevrimdışı',
};

export interface ActivityDateGroup {
  /** 'today' | 'yesterday' | 'thisWeek' | 'older' */
  bucket: 'today' | 'yesterday' | 'thisWeek' | 'older';
  /** Görüntülenecek başlık (Bugün, Dün, Bu hafta, Daha eski). */
  label: string;
  items: ActivityItem[];
}

function bucketFor(ts: number, now: number): ActivityDateGroup['bucket'] {
  const date = new Date(ts);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  if (ts >= today.getTime()) return 'today';
  const yesterday = today.getTime() - 86_400_000;
  if (ts >= yesterday) return 'yesterday';
  const weekAgo = today.getTime() - 7 * 86_400_000;
  if (ts >= weekAgo) return 'thisWeek';
  return 'older';
}

const BUCKET_LABELS: Record<ActivityDateGroup['bucket'], string> = {
  today: 'Bugün', yesterday: 'Dün', thisWeek: 'Bu hafta', older: 'Daha eski',
};

export type ActivityFilterKind = 'all' | 'call' | 'queue' | 'status';

export interface ActivityFeedResult {
  groups: ActivityDateGroup[];
  /** Toplam gösterilebilir item sayısı (filter sonrası). */
  total: number;
  /** Bugüne özel özet — header card için. */
  todaySummary: { calls: number; statusChanges: number; queueChanges: number; talkSec: number };
  isLoading: boolean;
}

/**
 * Tüm aktivite feed'ini fetch edip UI-ready hale getirir.
 *
 * - Backend'den ham log'ları çeker (ActivityLogs.listMine)
 * - call_* event'lerini callId etrafında collapse eder (tek satır per çağrı)
 * - queue/status event'lerini olduğu gibi ekler
 * - Filter chip'ine göre eler
 * - Tarih grup'larına böler (Bugün/Dün/Bu hafta/Daha eski)
 * - 10 sn'de bir auto-refresh (live update)
 */
export function useActivityFeed(filter: ActivityFilterKind = 'all', search = '', limit = 200): ActivityFeedResult {
  const backendOnline = useBackendOnline();
  const { data, isLoading } = useQuery({
    queryKey: ['activity-feed', limit],
    queryFn: () => ActivityLogs.listMine(limit),
    refetchInterval: 10_000,
    staleTime: 5_000,
    enabled: backendOnline, // backend offline'ken poll'u durdur (502 hammer yok)
  });

  if (isLoading || !data) {
    return { groups: [], total: 0, todaySummary: { calls: 0, statusChanges: 0, queueChanges: 0, talkSec: 0 }, isLoading: true };
  }

  // 0) ESKİDEN→YENİYE sırala. listMine DESC döner; collapse mantığı çağrı
  //    yaşam döngüsünü (incoming→answered→ended) artan sırada bekliyor. DESC'te
  //    call_ended/answered önce gelip `calls.has(key)` guard'ına takılıp atlanıyordu
  //    → her çağrı incoming_call'ın "cevap bekleniyor"unda kalıyordu.
  const ordered = [...data].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  // 1) Call event'lerini collapse et
  const callMap = collapseCallEvents(ordered);
  // 2) Diğer event'leri çıkar
  const others = extractNonCallEvents(ordered);
  // 3) Birleşik liste
  const all = [...Array.from(callMap.values()), ...others]
    .sort((a, b) => b.startedAt - a.startedAt);

  // 4) Bugün özeti hesapla (filter'dan ÖNCE — toplam gerçek metrik)
  const now = Date.now();
  const todayBucket = bucketFor(now, now);
  void todayBucket; // bucketFor için baseline
  let todayCalls = 0, todayStatus = 0, todayQueue = 0, todayTalkSec = 0;
  for (const item of all) {
    if (bucketFor(item.startedAt, now) !== 'today') continue;
    if (item.kind === 'call') {
      todayCalls += 1;
      todayTalkSec += item.durationSec ?? 0;
    } else if (item.kind === 'queue') {
      todayQueue += 1;
    } else if (item.kind === 'status') {
      todayStatus += 1;
    }
  }

  // 5) Filter uygula (kind + serbest metin arama)
  let filtered = filter === 'all' ? all : all.filter((i) => i.kind === filter);
  const q = search.trim().toLowerCase();
  if (q) {
    filtered = filtered.filter((i) =>
      i.primary.toLowerCase().includes(q) ||
      i.label.toLowerCase().includes(q) ||
      (i.secondary?.toLowerCase().includes(q) ?? false),
    );
  }

  // 6) Tarih grupları
  const groupMap = new Map<ActivityDateGroup['bucket'], ActivityItem[]>();
  for (const item of filtered) {
    const b = bucketFor(item.startedAt, now);
    if (!groupMap.has(b)) groupMap.set(b, []);
    groupMap.get(b)!.push(item);
  }
  const order: ActivityDateGroup['bucket'][] = ['today', 'yesterday', 'thisWeek', 'older'];
  const groups: ActivityDateGroup[] = order
    .filter((b) => groupMap.has(b))
    .map((b) => ({ bucket: b, label: BUCKET_LABELS[b], items: groupMap.get(b)! }));

  return {
    groups,
    total: filtered.length,
    todaySummary: { calls: todayCalls, statusChanges: todayStatus, queueChanges: todayQueue, talkSec: todayTalkSec },
    isLoading: false,
  };
}

function formatDur(sec: number): string {
  if (sec <= 0) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
}
